import React from 'react';
import { Button, parseSearch, useToast } from '@seccl/ui';
import type { GetServerSidePropsContext, InferGetServerSidePropsType, NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { getProtectedServerSession } from '../../api/serverSession';
import { Layout } from '../../components/layout/layout';
import { useBranding } from '../../context/branding.context';
import { routes } from '../../routes';
import { isSafeReturnPath } from '../../utils/returnPath';

import styles from './signin.module.scss';

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const protectedSession = await getProtectedServerSession(context);

  const decodedUrl = decodeURIComponent(context.resolvedUrl);
  const { returnUrl } = parseSearch<{ returnUrl: string }>(decodedUrl);

  if (context.query?.organization_name && protectedSession.hosts?.[0]) {
    return {
      redirect: {
        destination: `https://${protectedSession.hosts?.[0]}`,
        permanent: false,
      },
    };
  }

  const destination = isSafeReturnPath(returnUrl) ? returnUrl : routes.dashboard;

  if (protectedSession.type === 'AUTHENTICATED') {
    return {
      redirect: {
        destination,
        permanent: false,
      },
    };
  }
  return {
    props: {
      returnUrl: destination,
      branding: protectedSession.branding,
      ...(protectedSession.host ? { host: protectedSession.host } : {}),
    },
  };
};

const SignInPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ returnUrl }) => {
  const router = useRouter();
  const errorFromQuery = router.query.error;

  const { dispatchToast, closeToast } = useToast();
  const { getLogoUrl } = useBranding();
  const professionalLogo = getLogoUrl('professionalLogo');

  React.useEffect(() => {
    let toastId: number | string;
    if (errorFromQuery) {
      // Dispatch a toast to show the error message
      toastId = dispatchToast({
        variant: 'error',
        title: 'Authentication error',
        message: errorFromQuery as string,
      });

      // Remove error from query
      router.replace(routes.auth.signIn(), undefined, { shallow: true });
    }

    return () => closeToast(toastId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout title="Sign in" className={styles.layout}>
      <div className={styles.container}>
        <section className={styles.signin}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          {professionalLogo && <img className={styles.logo} src={professionalLogo} alt="Logo" />}
          <h1>Welcome</h1>
          <p>Click the button below to sign in</p>
          <Button LinkComponent={Link} className={styles.button} href={routes.auth.auth0.login(returnUrl)}>
            Sign in
          </Button>
        </section>
      </div>
    </Layout>
  );
};

export default SignInPage;
